import { useDownPayment } from '@finn/ua-featureflags';
import { useOpenModal } from '@finn/ua-modals';
import { TrackingEventName } from '@finn/ua-tracking';
import { UniversalRouter, useCurrentLocale } from '@finn/ui-utils';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useDiscoveryConfig } from '../../core/data/MetadataProvider';
import { GenericVehicleDetails } from '../../core/types';
import { calculateTotalPrice, getDefaultTerm } from '../../product-pricing';
import { triggerEcommerceOrderingTrackingEvent } from '../../tracking';
import { ConfigureState } from './ConfigurePageStoreTypes';

export const getCheckoutCartUrl = (
  vehicleId: string,
  term: number | string,
  kmPackage: number | string,
  downPaymentAmount?: number
): string => {
  if (downPaymentAmount) {
    return `/checkout/cart/${vehicleId}/${term}/${kmPackage}?downPaymentAmount=${downPaymentAmount}`;
  }

  return `/checkout/cart/${vehicleId}/${term}/${kmPackage}`;
};

// Default distance options for different regions.
export const defaultKmOption = 1000;

export const getShortestKmOption = (
  vehicle: GenericVehicleDetails,
  { forceRegularPrice, term }: { term?: number; forceRegularPrice?: boolean }
) => {
  if (forceRegularPrice && term && (vehicle.price?.[term] as any)?.extra_1000) {
    return 500;
  }

  if (vehicle.price.extra_1000) {
    return 500;
  }

  return defaultKmOption;
};

const initialState = {
  term: 0,
  monthlyPrice: 0,
  isLoading: false,
  downPayment: true,
  kilometerPackage: 0,
  downPaymentAmount: 1200,
  downPaymentEnabled: true,
};

const STORE_NAME = 'configure';
enum ActionNames {
  SET_TERM = 'setTerm',
  SET_DOWNPAYMENT = 'setDownPayment',
  SET_KILOMETER_PACKAGE = 'setKilometerPackage',
  ON_CONTINUE = 'onContinue',
  GO_TO_CART = 'goToCart',
  SET_INITIAL_TERM = 'setInitialTerm',
  SET_ON_CHECKOUT = 'setOnCheckout',
}

export const useConfigureStore = create<ConfigureState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      setTerm(term?: number) {
        set(
          ({ downPayment }) => ({
            term,
            downPaymentEnabled: term !== 1,
            downPayment: term !== 1 && downPayment,
          }),
          false,
          ActionNames.SET_TERM
        );
      },
      setKilometerPackage(kilometerPackage: number) {
        set(
          () => ({ kilometerPackage }),
          false,
          ActionNames.SET_KILOMETER_PACKAGE
        );
      },

      setDownPayment(downPayment: boolean) {
        set(() => ({ downPayment }), false, ActionNames.SET_DOWNPAYMENT);
      },

      setOnCheckout(onCheckout) {
        set(() => ({ onCheckout }), false, ActionNames.SET_ON_CHECKOUT);
      },

      async onContinue() {
        set(() => ({ isLoading: true }), false, ActionNames.ON_CONTINUE);

        const { goToCart } = get();

        await goToCart();
      },

      async goToCart({ isDirect } = {}) {
        set(() => ({ isLoading: false }), false, ActionNames.GO_TO_CART);
        const {
          vehicle,
          router,
          kilometerPackage,
          term,
          downPayment,
          onCheckout,
        } = get();
        const monthlyPrice = calculateTotalPrice({
          vehicle,
          kilometerPackage,
          term,
          isDownPayment: downPayment,
        });
        const monthlyPriceWithoutDownpayment = calculateTotalPrice({
          vehicle,
          kilometerPackage,
          term,
          isDownPayment: false,
        });

        const downPaymentAmountInVehicle =
          vehicle?.downpayment_prices?.downpayment_fixed_amount ||
          vehicle?.downpayment_prices?.fixed_amount;

        triggerEcommerceOrderingTrackingEvent({
          eventName: TrackingEventName.PRODUCT_ADDED,
          kmPackage: kilometerPackage,
          monthlyPrice,
          monthlyPriceWithoutDownpayment,
          downPaymentAmount: downPayment
            ? downPaymentAmountInVehicle
            : undefined,
          term,
          vehicle,
        });
        if (isDirect) return;

        const downPaymentAmount = downPayment
          ? downPaymentAmountInVehicle
          : undefined;

        const url = getCheckoutCartUrl(
          vehicle?.id ?? '',
          term,
          kilometerPackage,
          downPaymentAmount
        );

        if (onCheckout) {
          onCheckout({
            id: vehicle?.id,
            term,
            kilometerPackage,
            downPaymentAmount,
          });

          return;
        }
        router?.push(url);
      },

      setInitialTerm(initialTerm) {
        const { vehicle, router } = get();

        if (!vehicle) return;

        const defaultTerm = getDefaultTerm(vehicle);
        const queryTerm = router?.query?.term || initialTerm;

        const parsedTerm = queryTerm ? parseInt(queryTerm) : undefined;
        const isValidTerm =
          typeof parsedTerm === 'undefined'
            ? false
            : vehicle.available_terms.includes(parsedTerm);

        const term = (isValidTerm && parsedTerm) || defaultTerm;

        set(() => ({ term }), false, ActionNames.SET_INITIAL_TERM);
      },

      // Dependencies
      setOpenModal(openModal) {
        set(() => ({ openModal }));
      },

      async setVehicle(vehicle, locale, forceRegularPrice, initialTerm) {
        const kilometerPackage = getShortestKmOption(vehicle, {
          term: get().term || getDefaultTerm(vehicle),
          forceRegularPrice,
        });

        set(() => ({
          vehicle,
          locale,
          kilometerPackage,
          isLoading: false,
        }));

        get().setInitialTerm(initialTerm);
      },

      async setRouter(
        router: UniversalRouter,
        initialTerm?: string,
        fastest?: boolean
      ) {
        set(() => ({ router, fastest }));

        get().setInitialTerm(initialTerm);
      },
    }),
    { name: STORE_NAME }
  )
);

/**
 * Initializes the Configure Store while loading its dependencies (location store, vehicle context, modal ...)
 * */
export const useInitializeConfigureStore = (
  vehicle: GenericVehicleDetails,
  {
    onCheckout,
  }: {
    onCheckout?: ({
      id,
      term,
      kilometerPackage,
      downPaymentAmount,
    }: {
      id: any;
      term: any;
      kilometerPackage: any;
      downPaymentAmount: any;
    }) => void;
  } = {}
) => {
  const { setRouter, setVehicle, setOpenModal, setDownPayment, setOnCheckout } =
    useConfigureStore();
  const { forceRegularPrice } = useDiscoveryConfig();
  const { isDownPaymentEnabled: isDownPaymentEnabledRaw } = useDownPayment();
  const isDownPaymentEnabled = forceRegularPrice
    ? true
    : isDownPaymentEnabledRaw;

  const { locale } = useCurrentLocale();
  const { router } = useDiscoveryConfig();
  const searchParams = useSearchParams();
  const term = String(searchParams?.get('term'));

  const openModal = useOpenModal();

  useEffect(() => {
    setDownPayment(isDownPaymentEnabled);
  }, [isDownPaymentEnabled, setDownPayment]);

  useEffect(() => {
    setOnCheckout(onCheckout);
  }, [onCheckout, setOnCheckout]);

  useEffect(() => {
    setRouter(router, term);
  }, [router, setRouter, term]);

  useEffect(() => {
    setOpenModal(openModal);
  }, [openModal, setOpenModal]);

  useEffect(() => {
    if (!vehicle) {
      return;
    }

    setVehicle(vehicle, locale, forceRegularPrice, term);
  }, [locale, setVehicle, vehicle, forceRegularPrice, term]);
};
